import React, { useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Typography
} from '@material-ui/core';
import { Link as RouterLink, useParams } from 'react-router-dom';
import ThumbDownAltOutlinedIcon from '@material-ui/icons/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@material-ui/icons/ThumbUpAltOutlined';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchApplicationById,
  resetScorecardSaved,
  saveApplicationScorecard,
  scorecardSavedSelector,
  selectedApplicationErrorSelector,
  selectedApplicationLoadingSelector,
  selectedApplicationSelector,
  updateScorecardForm
} from '../../redux/features/applications/applicationsSlice';
import { messageUpdated } from '../../redux/features/app/appSlice';
import { Severity } from '../../components/Message/Message';
import { APP_PATH_APPLICATIONS } from '../../config';
import CandidateScorecard from '../../components/CandidateScorecard/CandidateScorecard';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  breadcrumbs: {
    marginBottom: theme.spacing(2)
  },
  title: {
    fontWeight: 600
  },
  content: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'center'
  },
  scorecardWrapper: {
    width: '100%',
    maxWidth: 720
  },
  historySection: {
    marginTop: theme.spacing(4)
  },
  historyCard: {
    marginTop: theme.spacing(2)
  },
  historyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  historyMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  historyNote: {
    whiteSpace: 'pre-wrap'
  },
  loadingState: {
    marginTop: theme.spacing(6),
    display: 'flex',
    justifyContent: 'center'
  }
}));

const ApplicationScorecardPage = () => {
  const classes = useStyles();
  const { applicationId } = useParams();
  const dispatch = useDispatch();
  const application = useSelector(selectedApplicationSelector);
  const loading = useSelector(selectedApplicationLoadingSelector);
  const loadError = useSelector(selectedApplicationErrorSelector);
  const scorecardSaved = useSelector(scorecardSavedSelector);
  const normalizedApplicationId = applicationId ? String(applicationId) : '';
  const normalizedSelectedId = application?.id ? String(application.id) : '';

  useEffect(() => {
    if (!applicationId || loading || loadError) {
      return;
    }

    if (
      normalizedSelectedId &&
      normalizedSelectedId === normalizedApplicationId
    ) {
      return;
    }

    dispatch(fetchApplicationById(applicationId));
  }, [
    applicationId,
    dispatch,
    loadError,
    loading,
    normalizedApplicationId,
    normalizedSelectedId
  ]);

  const candidate = useMemo(() => {
    if (!application) {
      return null;
    }
    return {
      id: application.userId,
      name: application.candidateName || 'Candidate',
      avatarUrl:
        application.candidateAvatarUrl || application.candidateAvatar || ''
    };
  }, [application]);

  const initialValue = {
    rating: 'very_good',
    feedbackText: ''
  };

  const handleSave = ({ rating, feedbackText }) => {
    if (!applicationId) {
      return;
    }
    // Update the rating a and feedback text
    dispatch(updateScorecardForm({ feedbackText }));
    dispatch(updateScorecardForm({ rating }));
    dispatch(saveApplicationScorecard(applicationId));
  };

  const candidateName = candidate?.name || 'Candidate';
  const scorecards = application?.scorecards?.length
    ? application.scorecards
    : application?.scorecard
    ? [application.scorecard]
    : [];
  const hasHistory = scorecards.length > 0;

  const ratingLabelMap = {
    very_poor: 'Very Poor',
    poor: 'Poor',
    good: 'Good',
    very_good: 'Very Good'
  };

  const ratingIconMap = {
    very_poor: <ThumbDownAltOutlinedIcon fontSize="small" />,
    poor: <ThumbDownAltOutlinedIcon fontSize="small" />,
    good: <ThumbUpAltOutlinedIcon fontSize="small" />,
    very_good: <ThumbUpAltOutlinedIcon fontSize="small" />
  };

  const formatDateTime = (timestamp) =>
    timestamp
      ? new Date(timestamp).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      : '';

  useEffect(() => {
    if (scorecardSaved) {
      dispatch(
        messageUpdated({
          text: 'Scorecard saved',
          severity: Severity.SUCCESS
        })
      );
      dispatch(resetScorecardSaved());
    }
  }, [dispatch, scorecardSaved]);

  return (
    <div className={classes.root}>
      {application && (
        <Breadcrumbs className={classes.breadcrumbs} aria-label="breadcrumb">
          <Link
            component={RouterLink}
            to={APP_PATH_APPLICATIONS}
            color="inherit">
            Applications
          </Link>
          <Typography color="textPrimary">{candidateName}</Typography>
          <Typography color="textPrimary">Scorecard</Typography>
        </Breadcrumbs>
      )}
      <Typography variant="h4" className={classes.title}>
        Candidate Evaluation
      </Typography>
      {loading ? (
        <div className={classes.loadingState}>
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.content}>
          <div className={classes.scorecardWrapper}>
            {application ? (
              <>
                <CandidateScorecard
                  candidate={candidate}
                  initialValue={initialValue}
                  onSave={handleSave}
                />
                <div className={classes.historySection}>
                  <Typography variant="h6">Scorecard History</Typography>
                  {hasHistory ? (
                    scorecards.map((scorecard) => (
                      <Card
                        key={scorecard.id}
                        className={classes.historyCard}
                        variant="outlined">
                        <CardContent>
                          <div className={classes.historyHeader}>
                            <div className={classes.historyMeta}>
                              <Chip
                                icon={ratingIconMap[scorecard.rating]}
                                label={
                                  ratingLabelMap[scorecard.rating] ||
                                  scorecard.rating
                                }
                                color={
                                  scorecard.rating === 'very_good'
                                    ? 'primary'
                                    : 'default'
                                }
                                size="small"
                              />
                              <Typography variant="body2" color="textSecondary">
                                {formatDateTime(scorecard.createdAt)}
                              </Typography>
                            </div>
                          </div>
                          <Divider />
                          <Box mt={2}>
                            <Typography
                              variant="body2"
                              className={classes.historyNote}
                              color="textSecondary">
                              {scorecard.feedbackText ||
                                'No feedback provided.'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No scorecards saved yet.
                    </Typography>
                  )}
                </div>
              </>
            ) : (
              <Typography color="textSecondary">
                {loadError
                  ? 'Unable to load application.'
                  : 'Application not found.'}
              </Typography>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationScorecardPage;
